/**
 * Camera Checkout selection page — intermediate full-bleed screen reached
 * when the user clicks the Launch icon on a row in the Checkouts list.
 *
 * Two tile choices:
 *   1. "I need a camera"   → (TODO) will eventually route to the pick-a-camera
 *                             / dock-selection flow. For now we continue to
 *                             the pairing page so the kiosk flow still works.
 *   2. "I have my camera"  → Goes directly to the existing pairing page
 *                             (/checkout/pair) to pair the camera with an
 *                             employee via org/site/user + PIN.
 *
 * Runs full-bleed (no portal sidebar/header) — wired up in App.jsx outside
 * the MainLayout wrapper.
 */
import { useNavigate, useSearchParams } from 'react-router-dom';

/** Resolve a public-folder asset URL respecting Vite's BASE_URL. */
const ICON_BASE =
  `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/icons/camera-checkout-page`;

export default function CameraCheckoutPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const checkoutId = params.get('checkoutId');
  const orgName  = params.get('org')  || '';
  const siteName = params.get('site') || '';
  const orgId    = params.get('orgId')  || '';
  const siteId   = params.get('siteId') || '';

  // Forward checkoutId + org + site so downstream pages keep the same context.
  const buildQs = () => {
    const qp = new URLSearchParams();
    if (checkoutId) qp.set('checkoutId', checkoutId);
    if (orgName)    qp.set('org',    orgName);
    if (siteName)   qp.set('site',   siteName);
    if (orgId)      qp.set('orgId',  orgId);
    if (siteId)     qp.set('siteId', siteId);
    const s = qp.toString();
    return s ? `?${s}` : '';
  };

  const goPair           = () => navigate(`/checkout/pair${buildQs()}`);
  const handleNeedCamera = () => navigate(`/checkout/need-camera${buildQs()}`);
  const handleHaveCamera = () => goPair();

  return (
    <div className="ck-page">
      <header className="ck-header">
        <div className="cc-brand">
          {orgName || 'Organization'}
          {siteName && <span className="cc-brand-site">/{siteName}</span>}
        </div>
        <div className="cc-header-actions">
          <button
            type="button"
            className="cc-header-btn"
            title="Exit"
            onClick={() => navigate('/checkout')}
          >
            <img src={`${ICON_BASE}/signout.svg`} alt="" className="cc-header-icon-img" />
          </button>
          <button type="button" className="cc-header-btn" title="Help">
            <img src={`${ICON_BASE}/questionmark.svg`} alt="" className="cc-header-icon-img" />
          </button>
          <button type="button" className="cc-header-btn cc-header-lang">
            English <i className="fas fa-chevron-down"></i>
          </button>
        </div>
      </header>

      <main className="cc-main">
        <h1 className="cc-title">Camera checkout option</h1>
        <p className="cc-subtitle">Select if you need or have a camera</p>

        <div className="cc-tile-grid">
          <button type="button" className="cc-tile" onClick={handleNeedCamera}>
            <div className="cc-tile-icon" aria-hidden="true">
              <img src={`${ICON_BASE}/option-i-need-a-camera.png`} alt="" />
            </div>
            <div className="cc-tile-label">
              I need a camera <span className="cc-tile-arrow" aria-hidden="true">→</span>
            </div>
          </button>

          <button type="button" className="cc-tile" onClick={handleHaveCamera}>
            <div className="cc-tile-icon" aria-hidden="true">
              <img src={`${ICON_BASE}/option-i-have-a-camera.png`} alt="" />
            </div>
            <div className="cc-tile-label">
              I have a camera <span className="cc-tile-arrow" aria-hidden="true">→</span>
            </div>
          </button>
        </div>
      </main>
    </div>
  );
}

